import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FlaskConical,
  Mail,
  Lock,
  ArrowRight,
  Sparkles,
  Shield,
  Zap,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuthStore } from '@/lib/store';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

const features = [
  {
    icon: Sparkles,
    title: 'Smart Matching',
    description: 'AI-powered matching connects you with the perfect studies based on your profile.',
  },
  {
    icon: Shield,
    title: 'Reliability Scoring',
    description: 'Build your reputation and unlock premium, higher-paying research opportunities.',
  },
  {
    icon: Zap,
    title: 'Instant Booking',
    description: 'Browse, book, and get paid — all in one seamless experience.',
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen flex overflow-hidden">
      {/* ───── Animated background ───── */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-brand-600/20 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-accent-600/15 via-transparent to-transparent" />

      {/* Floating orbs */}
      <div className="absolute top-20 left-[15%] w-72 h-72 bg-brand-500/10 rounded-full blur-3xl animate-pulse-soft" />
      <div className="absolute bottom-20 right-[10%] w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-brand-400/5 rounded-full blur-3xl animate-pulse-soft" style={{ animationDelay: '0.5s' }} />

      {/* Grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        }}
      />

      {/* ───── Left Hero ───── */}
      <div className="hidden lg:flex flex-1 relative flex-col justify-center px-12 xl:px-20">
        <motion.div
          initial="hidden"
          animate="visible"
          className="max-w-lg"
        >
          {/* Brand */}
          <motion.div variants={fadeUp} custom={0} className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center shadow-2xl shadow-brand-500/30">
              <FlaskConical className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">APEX</h1>
              <p className="text-xs text-gray-400 font-medium tracking-widest uppercase">Research Marketplace</p>
            </div>
          </motion.div>

          {/* Tagline */}
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight mb-6"
          >
            Get Paid for Research.{' '}
            <span className="gradient-text">Get Reliable Participants.</span>
          </motion.h2>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-lg text-gray-400 leading-relaxed mb-12"
          >
            The trusted platform connecting UCI researchers with verified student
            participants. Fast, fair, and built for science.
          </motion.p>

          {/* Feature bullets */}
          <div className="space-y-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                variants={fadeUp}
                custom={3 + i}
                className="flex items-start gap-4 group"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-brand-500/10 group-hover:border-brand-500/30 transition-all duration-300">
                  <feature.icon className="w-5 h-5 text-brand-400" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">{feature.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ───── Right Login Card ───── */}
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-12 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key="login"
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="w-full max-w-md"
          >
            {/* Mobile brand (visible on < lg) */}
            <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center shadow-lg shadow-brand-500/25">
                <FlaskConical className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-white tracking-tight">APEX</h1>
            </div>

            {/* Glass card */}
            <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-2xl shadow-black/30 p-8 sm:p-10">
              {/* Top glow */}
              <div className="absolute -top-px left-8 right-8 h-px bg-gradient-to-r from-transparent via-brand-400/50 to-transparent" />

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white tracking-tight mb-2">Welcome back</h2>
                <p className="text-sm text-gray-400">Sign in to your APEX account to continue</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@uci.edu"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field pl-11 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field pl-11 pr-12 bg-white/5 border-white/10 text-white placeholder:text-gray-600 focus:border-brand-500/60 focus:ring-brand-500/30"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full text-base"
                >
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </div>
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 text-gray-500 bg-transparent">New to APEX?</span>
                </div>
              </div>

              {/* Register link */}
              <Link
                to="/register"
                className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl font-semibold text-sm text-gray-300 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-200 group"
              >
                Create an account
                <ArrowRight className="w-4 h-4 text-gray-500 group-hover:text-brand-400 group-hover:translate-x-0.5 transition-all duration-200" />
              </Link>
            </div>

            {/* Footer text */}
            <p className="text-center text-xs text-gray-600 mt-6">
              By signing in, you agree to APEX's Terms of Service and Privacy Policy.
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
